/**
 * Pure layout functions for the canvas whiteboard.
 *
 * Two modes:
 *  - Masonry: column-stagger fallback for blocks without server-side
 *    canvas_meta; server-positioned cards reserve their space first.
 *  - Timeline: real-time x-axis via scaleTime over `created_at`
 *    (Issue #10), with greedy first-fit lane-packing for overlapping
 *    time-clusters. Falls back to a sequential dense-pack when any
 *    block lacks a timestamp (legacy posts) — in that case no axis
 *    geometry is returned, because the x-positions carry no
 *    time-semantics.
 *
 * Both functions are pure: (blocks, geometry-in) → (rects, geometry-
 * out). No refs, no React. The component wraps them in useMemo.
 */

import { textBlockId } from '@arkturian/canvas-engine'
import type { CanvasBlock } from './mergeBlocks'

export interface CardRect {
  x: number
  y: number
  width: number
  height: number
}

export type LayoutRects = Record<string, CardRect>

// --- Shared card metrics -------------------------------------------------

export const CARD_W = 280
export const CARD_H_MIN = 56
export const CARD_H_MAX = 600

/**
 * Char-count → approximate card height. Only a bootstrap estimate for
 * cards that haven't reported a measured DOM height yet — markdown
 * syntax inflates char_count far beyond the rendered text, so layouts
 * MUST prefer `measuredHeights` once available or columns show large
 * phantom gaps below short cards.
 */
export function heightForChars(chars: number): number {
  const approxLines = Math.max(1, Math.ceil(chars / 38))
  const h = 16 + approxLines * 20
  return Math.max(CARD_H_MIN, Math.min(CARD_H_MAX, h))
}

/** Real DOM heights reported per textBlockId via ResizeObserver. */
export type MeasuredHeights = Record<string, number>

function cardHeight(
  b: CanvasBlock,
  measured: MeasuredHeights | undefined,
): number {
  const m = measured?.[textBlockId(b)]
  return m != null && m > 0 ? m : heightForChars(b.char_count)
}

// --- Masonry -------------------------------------------------------------

const COLS = 4
const COL_GAP = 16
const ROW_GAP = 12

/**
 * Column-stagger layout. Server-positioned cards (canvas_meta) keep
 * their stored rect and reserve the space (pass 1); unpositioned cards
 * fill the shortest column below the occupied space (pass 2). Baseline
 * positions are frozen per blocks-array — drag overrides live in
 * component state so dragging one card never reshuffles the others.
 */
export function computeMasonryLayout(
  blocks: CanvasBlock[],
  measured?: MeasuredHeights,
): LayoutRects {
  const colY = new Array<number>(COLS).fill(0)
  const colXs = new Array<number>(COLS)
    .fill(0)
    .map((_, c) => c * (CARD_W + COL_GAP))

  const pushColY = (rect: CardRect) => {
    for (let c = 0; c < COLS; c++) {
      const left = colXs[c]
      const right = left + CARD_W
      if (rect.x < right && rect.x + rect.width > left) {
        const bottom = rect.y + rect.height + ROW_GAP
        if (bottom > colY[c]) colY[c] = bottom
      }
    }
  }

  const out: LayoutRects = {}
  for (const b of blocks) {
    if (b.canvas_meta) {
      const r = {
        x: b.canvas_meta.x,
        y: b.canvas_meta.y,
        width: b.canvas_meta.width,
        height: b.canvas_meta.height,
      }
      out[textBlockId(b)] = r
      pushColY(r)
    }
  }
  for (const b of blocks) {
    if (b.canvas_meta) continue
    let col = 0
    for (let i = 1; i < COLS; i++) if (colY[i] < colY[col]) col = i
    const r = {
      x: colXs[col],
      y: colY[col],
      width: CARD_W,
      height: cardHeight(b, measured),
    }
    out[textBlockId(b)] = r
    pushColY(r)
  }
  return out
}

// --- Timeline ------------------------------------------------------------

const TL_CARD_W = 240
const TL_GAP_X = 16
const TL_LANE_GAP = 12 // horizontal gap between cards in the same lane
const TL_LANE_VGAP = 16 // vertical gap between stacked cards
const TL_HEADER_Y = 56
const TL_X_PAD = 40

// Gap-collapse (Tree IACP 38007966 Option B): message threads pause
// for hours, then burst — a strictly linear axis lets the empty time
// dominate the canvas. Idle stretches longer than GAP_COLLAPSE_MS
// fold into a fixed GAP_COLLAPSED_PX band with a pause marker; time
// stays linear inside the active segments.
const GAP_COLLAPSE_MS = 30 * 60_000
const GAP_COLLAPSED_PX = 72

/** One linearly-mapped stretch of active time. */
export interface TimeSegment {
  tStart: number
  tEnd: number
  xStart: number
  xEnd: number
}

/** A collapsed idle stretch between two segments. */
export interface TimelineGap {
  /** Center x of the collapsed band (world-space). */
  x: number
  /** Band width in px. */
  width: number
  /** Human label, e.g. "2h Pause". */
  label: string
}

/**
 * Piecewise time→x axis emitted by the timeline layout when (and only
 * when) every block carries a real `created_at`. Tick-building reads
 * THIS instead of re-deriving the mapping — axis and cards stay
 * aligned by construction.
 */
export interface TimelineAxis {
  segments: TimeSegment[]
  gaps: TimelineGap[]
}

/** Map a timestamp through the piecewise axis (clamps to the ends). */
export function timeToX(axis: TimelineAxis, t: number): number {
  const segs = axis.segments
  if (segs.length === 0) return 0
  if (t <= segs[0].tStart) return segs[0].xStart
  for (const s of segs) {
    if (t <= s.tEnd) {
      if (t <= s.tStart) return s.xStart
      const span = Math.max(s.tEnd - s.tStart, 1)
      return s.xStart + ((t - s.tStart) / span) * (s.xEnd - s.xStart)
    }
  }
  return segs[segs.length - 1].xEnd
}

function fmtDuration(ms: number): string {
  const MIN = 60_000
  const HOUR = 60 * MIN
  const DAY = 24 * HOUR
  if (ms >= DAY) return `${Math.round(ms / DAY)}d Pause`
  if (ms >= HOUR) return `${Math.round(ms / HOUR)}h Pause`
  return `${Math.round(ms / MIN)}m Pause`
}

/**
 * Build the piecewise axis: split the sorted timestamps into segments
 * at gaps > GAP_COLLAPSE_MS, distribute the available pixel span over
 * the ACTIVE time only, and reserve a fixed band per collapsed gap.
 */
function buildAxis(sortedTimes: number[], xSpanPx: number): TimelineAxis {
  // Segment boundaries.
  const bounds: Array<{ tStart: number; tEnd: number }> = []
  let segStart = sortedTimes[0]
  let prev = sortedTimes[0]
  for (const t of sortedTimes) {
    if (t - prev > GAP_COLLAPSE_MS) {
      bounds.push({ tStart: segStart, tEnd: prev })
      segStart = t
    }
    prev = t
  }
  bounds.push({ tStart: segStart, tEnd: prev })

  const gapCount = bounds.length - 1
  const activeMs = bounds.reduce((s, b) => s + (b.tEnd - b.tStart), 0)
  // Guarantee some width per segment even when gaps eat the viewport —
  // the canvas pans, overflowing right is fine.
  const availablePx = Math.max(
    xSpanPx - gapCount * GAP_COLLAPSED_PX,
    bounds.length * 80,
  )
  const pxPerMs = availablePx / Math.max(activeMs, 1)

  const segments: TimeSegment[] = []
  const gaps: TimelineGap[] = []
  let x = TL_X_PAD
  for (let i = 0; i < bounds.length; i++) {
    const b = bounds[i]
    const w = (b.tEnd - b.tStart) * pxPerMs
    segments.push({ tStart: b.tStart, tEnd: b.tEnd, xStart: x, xEnd: x + w })
    x += w
    if (i < bounds.length - 1) {
      gaps.push({
        x: x + GAP_COLLAPSED_PX / 2,
        width: GAP_COLLAPSED_PX,
        label: fmtDuration(bounds[i + 1].tStart - b.tEnd),
      })
      x += GAP_COLLAPSED_PX
    }
  }
  return { segments, gaps }
}

export interface TimelineLayoutResult {
  rects: LayoutRects
  /** null when the sequential fallback ran (no time-semantics). */
  axis: TimelineAxis | null
}

/**
 * Time-axis layout. `viewportWidth` spans the axis across the visible
 * canvas at scale=1.0 (cards stay at readable size).
 *
 * Vertical packing is per-card drop-down (skyline), NOT lane rows:
 * x is pinned by scaleTime, then each card falls from the header until
 * it rests on the lowest horizontally-overlapping card already placed.
 * The earlier lane-row scheme sized every row to its tallest card, so
 * one 600px card anywhere in a lane pushed the WHOLE next row 600px
 * down — short cards showed huge phantom gaps below them (same class
 * of bug as the masonry estimate-gaps). Drop-down stacks only where
 * time-overlap actually exists and lets everything else float up.
 */
export function computeTimelineLayout(
  blocks: CanvasBlock[],
  viewportWidth: number,
  measured?: MeasuredHeights,
): TimelineLayoutResult {
  const xSpanPx = Math.max(viewportWidth - 2 * TL_X_PAD, 600)

  const blocksWithTime = blocks.map((b) => ({
    block: b,
    t: b.created_at ? Date.parse(b.created_at) : NaN,
  }))
  const allHaveTime =
    blocks.length > 0 && blocksWithTime.every(({ t }) => Number.isFinite(t))

  const rects: LayoutRects = {}
  const placedRects: CardRect[] = []

  // Drop the card straight down from the header until it collides with
  // no horizontally-overlapping placed card. y only ever grows, so the
  // settle loop terminates.
  const settleY = (x: number, height: number): number => {
    const overlapping = placedRects.filter(
      (r) => x < r.x + r.width + TL_LANE_GAP && x + TL_CARD_W + TL_LANE_GAP > r.x,
    )
    let y = TL_HEADER_Y
    let moved = true
    while (moved) {
      moved = false
      for (const r of overlapping) {
        if (y < r.y + r.height + TL_LANE_VGAP && y + height + TL_LANE_VGAP > r.y) {
          y = r.y + r.height + TL_LANE_VGAP
          moved = true
        }
      }
    }
    return y
  }

  const place = (b: CanvasBlock, x: number) => {
    const height = cardHeight(b, measured)
    const rect = {
      x,
      y: settleY(x, height),
      width: TL_CARD_W,
      height,
    }
    rects[textBlockId(b)] = rect
    placedRects.push(rect)
  }

  let axis: TimelineAxis | null = null
  if (allHaveTime) {
    const sorted = [...blocksWithTime].sort((a, b) =>
      a.t !== b.t
        ? a.t - b.t
        : a.block.paragraph_index - b.block.paragraph_index,
    )
    axis = buildAxis(sorted.map((s) => s.t), xSpanPx)
    for (const { block, t } of sorted) {
      place(block, timeToX(axis, t))
    }
  } else {
    // Sequential dense-pack fallback — no time-semantics promised.
    const sorted = [...blocks].sort(
      (a, b) => a.paragraph_index - b.paragraph_index,
    )
    let cursorX = TL_X_PAD
    for (const b of sorted) {
      place(b, cursorX)
      cursorX += TL_CARD_W + TL_GAP_X
    }
  }

  return { rects, axis }
}

// --- Timeline axis ticks -------------------------------------------------

export interface TimelineTick {
  x: number
  label: string
}

/**
 * Build time-labeled ticks from the axis geometry the layout emitted.
 * Interval auto-picked so ~8-12 ticks span the range; labels show
 * HH:mm for sub-day spans, DD.MM HH:mm beyond. Returns [] for a null
 * axis (sequential fallback — an axis would lie about time).
 */
export function buildTimelineTicks(axis: TimelineAxis | null): TimelineTick[] {
  if (!axis || axis.segments.length === 0) return []
  const tMin = axis.segments[0].tStart
  const tMax = axis.segments[axis.segments.length - 1].tEnd

  const MIN = 60_000
  const HOUR = 60 * MIN
  const DAY = 24 * HOUR
  // Tick interval sized to the ACTIVE time (collapsed gaps carry their
  // own pause markers — ticks inside a gap would be meaningless).
  const activeMs = axis.segments.reduce((s, b) => s + (b.tEnd - b.tStart), 0)
  const intervals = [MIN, 5 * MIN, 15 * MIN, HOUR, 6 * HOUR, DAY, 7 * DAY]
  let chosen = intervals[intervals.length - 1]
  for (const ms of intervals) {
    if (Math.max(activeMs, 1) / ms <= 12) {
      chosen = ms
      break
    }
  }

  const showDate = tMax - tMin > DAY
  const fmt = (t: number): string => {
    const d = new Date(t)
    const hh = String(d.getHours()).padStart(2, '0')
    const mm = String(d.getMinutes()).padStart(2, '0')
    if (!showDate) return `${hh}:${mm}`
    const DD = String(d.getDate()).padStart(2, '0')
    const MM = String(d.getMonth() + 1).padStart(2, '0')
    return `${DD}.${MM} ${hh}:${mm}`
  }

  const ticks: TimelineTick[] = []
  for (const seg of axis.segments) {
    // Every segment opens with a tick at its start — a burst-cluster
    // shorter than the interval would otherwise have no label at all.
    ticks.push({ x: seg.xStart, label: fmt(seg.tStart) })
    const first = Math.ceil(seg.tStart / chosen) * chosen
    for (let t = first; t <= seg.tEnd; t += chosen) {
      const x = timeToX(axis, t)
      // Skip interval ticks that would crowd the segment-start label.
      if (x - seg.xStart < 56) continue
      ticks.push({ x, label: fmt(t) })
    }
  }
  return ticks
}
